import { ReactNode, useEffect, useRef, useState } from 'react';
import { Depth, usePrefs } from '../hooks';
import { DEPTH_LABELS } from '../copy';
import { CloseIcon, CompareIcon, KeysIcon, PatternsIcon, PiecesIcon, SearchIcon, SlidersIcon, StatsIcon } from './Icons';
import { search, SearchKind } from '../../data/search';
import { PRELUDE_ENTRIES, SONATA_ENTRIES } from '../../data/chorales/corpusIndex';

/** The main sections. `views` are the routes that belong to each (for the highlighted tab). */
export const SECTIONS: Array<{ href: string; label: string; views: string[]; icon: () => ReactNode }> = [
  { href: '#library', label: 'Pieces', views: ['piece', 'library', 'keys'], icon: PiecesIcon },
  { href: '#compare', label: 'Compare', views: ['compare', 'passion', 'tunes', 'tune', 'variations', 'returns', 'forms', 'form'], icon: CompareIcon },
  { href: '#paths', label: 'Patterns', views: ['paths', 'path', 'feel'], icon: PatternsIcon },
  { href: '#observatory', label: 'Statistics', views: ['observatory'], icon: StatsIcon },
  { href: '#walk', label: 'Play', views: ['walk', 'train'], icon: KeysIcon },
];

export function TopNav({ view }: { view: string }) {
  return (
    <nav className="nav" aria-label="Main">
      {SECTIONS.map((n) => (
        <a key={n.href} href={n.href} aria-current={n.views.includes(view) ? 'page' : undefined}>
          {n.label}
        </a>
      ))}
      <a href="#method" aria-current={view === 'method' ? 'page' : undefined}>
        About
      </a>
    </nav>
  );
}

/** On phones the sections move to a bar at the bottom of the screen, within reach of a thumb. */
export function TabBar({ view }: { view: string }) {
  return (
    <nav className="tabbar" aria-label="Sections">
      {SECTIONS.map((n) => {
        const Icon = n.icon;
        return (
          <a key={n.href} href={n.href} aria-current={n.views.includes(view) ? 'page' : undefined}>
            <Icon />
            <span>{n.label}</span>
          </a>
        );
      })}
    </nav>
  );
}

/** The header button that says how much theory is showing, and opens the settings. */
export function SettingsButton() {
  const { prefs } = usePrefs();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onNav = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('hashchange', onNav);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('hashchange', onNav);
    };
  }, [open]);
  return (
    <div className="settings" ref={wrap}>
      <button className="settings-btn" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((o) => !o)} title="How much theory to show, and the sound">
        <SlidersIcon />
        <span className="settings-now">
          <span className="settings-kicker">Theory:</span> {DEPTH_LABELS[prefs.depth].short}
        </span>
      </button>
      {open && <SettingsSheet onClose={() => setOpen(false)} />}
    </div>
  );
}

function SettingsSheet({ onClose }: { onClose: () => void }) {
  const { prefs, setPrefs } = usePrefs();
  return (
    <div className="sheet" role="dialog" aria-label="Settings">
      <div className="sheet-head">
        <strong>Settings</strong>
        <button className="btn quiet icon" onClick={onClose} aria-label="Close settings">
          <CloseIcon />
        </button>
      </div>
      <fieldset className="sheet-group">
        <legend>How much theory to show</legend>
        {([0, 1, 2, 3, 4] as Depth[]).map((d) => (
          <label key={d} className="level" data-on={prefs.depth === d}>
            <input type="radio" name="depth" id={`depth-${d}`} checked={prefs.depth === d} onChange={() => setPrefs({ depth: d })} />
            <span>
              <b>{DEPTH_LABELS[d].short}</b>
              <small>{DEPTH_LABELS[d].long}</small>
            </span>
          </label>
        ))}
      </fieldset>
      {prefs.depth >= 3 && (
        <fieldset className="sheet-group">
          <legend>Chord symbols</legend>
          <div className="seg" role="group" aria-label="Chord symbols">
            <button aria-pressed={prefs.lens === 'roman'} onClick={() => setPrefs({ lens: 'roman' })}>
              Roman numerals
            </button>
            <button aria-pressed={prefs.lens === 'figured'} onClick={() => setPrefs({ lens: 'figured' })}>
              Figured bass
            </button>
          </div>
        </fieldset>
      )}
      <fieldset className="sheet-group">
        <legend>Sound</legend>
        <div className="seg" role="group" aria-label="Instrument">
          <button aria-pressed={prefs.timbre === 'piano'} onClick={() => setPrefs({ timbre: 'piano' })}>
            Piano
          </button>
          <button aria-pressed={prefs.timbre === 'harpsichord'} onClick={() => setPrefs({ timbre: 'harpsichord' })}>
            Harpsichord
          </button>
        </div>
      </fieldset>
      <p className="sheet-foot">
        <a href="#method" onClick={onClose}>
          How to use the atlas
        </a>
      </p>
    </div>
  );
}

/** Where this page sits: "Compare › Theme and variations › K. 265". The last item is the page itself. */
export function Crumbs({ items }: { items: Array<{ href?: string; label: string }> }) {
  return (
    <nav className="crumbs" aria-label="You are here">
      {items.map((it, i) => (
        <span key={i}>
          {i > 0 && <span className="crumb-sep" aria-hidden="true">›</span>}
          {it.href ? <a href={it.href}>{it.label}</a> : <span aria-current="page">{it.label}</span>}
        </span>
      ))}
    </nav>
  );
}

/** Tabs between the pages of one section (Compare, Patterns). */
export function SubTabs({ items, current }: { items: Array<{ href: string; label: string; id: string; hidden?: boolean }>; current: string }) {
  return (
    <nav className="subtabs" aria-label="In this section">
      {items
        .filter((x) => !x.hidden)
        .map((x) => (
          <a key={x.id} href={x.href} aria-current={x.id === current ? 'page' : undefined}>
            {x.label}
          </a>
        ))}
    </nav>
  );
}

const KIND_LABEL: Record<SearchKind, string> = { page: 'Page', piece: 'Piece', tune: 'Tune', pattern: 'Pattern', feeling: 'Feeling' };

/** Find anything in the atlas: a button in the header, or press / anywhere. */
export function SearchButton() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setOpen(true);
      }
    };
    // Going somewhere else (a link, Back) closes the dialog.
    const onNav = () => setOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('hashchange', onNav);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('hashchange', onNav);
    };
  }, []);
  return (
    <>
      <button className="settings-btn search-btn" onClick={() => setOpen(true)} aria-label="Search the atlas" title="Search the atlas (/)">
        <SearchIcon />
        <span className="search-word">Search</span>
      </button>
      {open && <SearchDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function SearchDialog({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const results = search(q, 40);
  // Focus the field on open; give focus back to whatever had it (the Search button, usually) on close.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    input.current?.focus();
    return () => {
      if (before && document.contains(before)) before.focus();
    };
  }, []);
  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`#sr-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);
  const go = (href: string) => {
    onClose();
    window.location.hash = href.slice(1);
  };
  const onFieldKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(results.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Home' && results.length && e.ctrlKey) {
      setActive(0);
    } else if (e.key === 'Enter' && results[active]) {
      e.preventDefault();
      go(results[active].href);
    }
  };
  // Escape closes from anywhere in the dialog; Tab stays inside it.
  const onPanelKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'Tab' && panel.current) {
      const f = [...panel.current.querySelectorAll<HTMLElement>('input, button, [href]')].filter((x) => !x.hasAttribute('disabled'));
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  const open = results.length > 0;
  return (
    <div className="search-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="search-panel" role="dialog" aria-modal="true" aria-label="Search the atlas" ref={panel} onKeyDown={onPanelKey}>
        <div className="search-field">
          <SearchIcon />
          <input
            ref={input}
            id="site-search"
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls="search-results"
            aria-activedescendant={open ? `sr-${active}` : undefined}
            aria-describedby="search-status"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onFieldKey}
            placeholder="A piece, BWV or K. number, tune, pattern or feeling"
            aria-label="Search"
            autoComplete="off"
            spellCheck={false}
          />
          <button className="btn quiet icon" onClick={onClose} aria-label="Close search">
            <CloseIcon />
          </button>
        </div>
        <p className="search-hint" id="search-status" aria-live="polite">
          {!q.trim()
            ? `Try ${[
                'Twinkle',
                SONATA_ENTRIES.length ? '545' : '244',
                'Passion',
                'Prinner',
                'waiting',
                PRELUDE_ENTRIES.length ? 'prelude in B minor' : 'D minor',
              ]
                .map((x) => `“${x}”`)
                .join(', ')
                .replace(/, ([^,]*)$/, ' or $1')}.`
            : !results.length
              ? 'Nothing found. Try fewer words, a catalogue number, or a key such as “D minor”.'
              : `${results.length === 40 ? '40 or more' : results.length} found. Up and down arrows to choose, Enter to open.`}
        </p>
        <ul className="search-results" id="search-results" role="listbox" aria-label="Results" ref={list} hidden={!open}>
          {results.map((r, i) => (
            <li
              key={`${r.href}-${i}`}
              id={`sr-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseMove={() => i !== active && setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => go(r.href)}
            >
              <span className="search-kind">{KIND_LABEL[r.kind]}</span>
              <span className="search-title">{r.title}</span>
              <span className="search-meta">{r.meta}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
